/**
 * Purpose: Pure geometry for the scanner's highlight frame — turns ZXing result points into a
 * box over the barcode and maps it from video pixels to the on-screen (object-cover) preview.
 * Inputs: ZXing-style result points (x/y in video pixels), the video's intrinsic size, and the
 * size of the container the video is stretched over with `object-fit: cover`.
 * Outputs: `boxFromPoints` → a normalised (0–1) box or null; `mapBoxToContainer` → a pixel
 * rect relative to the container, clamped to its bounds.
 */

export type ResultPointLike = { getX(): number; getY(): number }

/** A box as fractions (0–1) of the video frame, so it survives any preview size. */
export type NormalizedBox = { x: number; y: number; width: number; height: number }

export type PixelRect = { left: number; top: number; width: number; height: number }

// Padding around the raw points so the frame sits just outside the bars, not on them.
const BOX_PADDING = 0.12
// A 1D code reports two points on one scanline; give it a plausible height from its width.
const LINEAR_HEIGHT_RATIO = 0.45
const MIN_BOX_FRACTION = 0.08

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n))
}

/** Bounding box of the decoded code, or null when ZXing gave us nothing usable to draw. */
export function boxFromPoints(
  points: readonly ResultPointLike[] | null | undefined,
  videoWidth: number,
  videoHeight: number,
): NormalizedBox | null {
  if (!points?.length || videoWidth <= 0 || videoHeight <= 0) return null

  const xs = points.map((p) => p.getX())
  const ys = points.map((p) => p.getY())
  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  let minY = Math.min(...ys)
  let maxY = Math.max(...ys)

  // Two collinear points (1D barcode): grow the box vertically around the scanline.
  const width = maxX - minX
  if (maxY - minY < width * 0.1) {
    const half = (width * LINEAR_HEIGHT_RATIO) / 2
    minY -= half
    maxY += half
  }

  const padX = (maxX - minX) * BOX_PADDING
  const padY = (maxY - minY) * BOX_PADDING
  const left = clamp01((minX - padX) / videoWidth)
  const top = clamp01((minY - padY) / videoHeight)
  const right = clamp01((maxX + padX) / videoWidth)
  const bottom = clamp01((maxY + padY) / videoHeight)

  const box = { x: left, y: top, width: right - left, height: bottom - top }
  if (box.width < MIN_BOX_FRACTION || box.height < MIN_BOX_FRACTION) return null
  return box
}

/** Where a normalised video box lands inside a container the video covers (object-fit: cover). */
export function mapBoxToContainer(
  box: NormalizedBox,
  videoWidth: number,
  videoHeight: number,
  containerWidth: number,
  containerHeight: number,
): PixelRect {
  // Cover scales by the larger ratio and crops the overflow evenly on the other axis.
  const scale = Math.max(containerWidth / videoWidth, containerHeight / videoHeight)
  const offsetX = (videoWidth * scale - containerWidth) / 2
  const offsetY = (videoHeight * scale - containerHeight) / 2

  const left = Math.max(0, box.x * videoWidth * scale - offsetX)
  const top = Math.max(0, box.y * videoHeight * scale - offsetY)
  const right = Math.min(containerWidth, (box.x + box.width) * videoWidth * scale - offsetX)
  const bottom = Math.min(containerHeight, (box.y + box.height) * videoHeight * scale - offsetY)

  return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) }
}
